import { useEffect, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { sheetFetch } from '@/lib/sheetFetch'
import type { AssigneeOption } from '@/types/work'

// Inline assignee editor used inside TaskDetailSheet's header. PATCHes
// /work/tasks/:id/reassign via sheetFetch, which routes through the
// ReassignTask interaction (parameterized policy + bypass-branch
// classification + assignee notification).
//
// Optimistic UI: the new label appears immediately; on a non-2xx
// response (validation, hierarchy denial, missing user) the previous
// value is restored and onAfterChange is NOT called.
//
// "Non assigné" is intentionally NOT offered — ReassignTask requires
// a target user. To unassign, use the standalone Edit page (where
// UpdateTask + assignee_user_id null is the supported path).

interface InlineAssigneeEditProps {
  taskId: number
  value: number | null
  options: AssigneeOption[]
  // Whether the actor can reassign at all. When false (or no options),
  // renders as plain text. Server is authoritative — every PATCH still
  // hits TaskPolicy#reassign? before reaching ReassignTask.
  canReassign: boolean
  // Plain-text fallback when canReassign is false.
  currentLabel: string
  onAfterChange: () => void
}

export function InlineAssigneeEdit({
  taskId,
  value,
  options,
  canReassign,
  currentLabel,
  onAfterChange,
}: InlineAssigneeEditProps) {
  const [current, setCurrent] = useState<number | null>(value)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setCurrent(value)
  }, [value])

  if (!canReassign || options.length === 0) {
    return <span className="text-foreground">{currentLabel}</span>
  }

  async function handleChange(next: string) {
    const nextId = Number(next)
    if (nextId === current) return
    const previous = current
    setCurrent(nextId)
    setPending(true)

    try {
      const response = await sheetFetch(`/work/tasks/${taskId}/reassign`, {
        method: 'PATCH',
        body: { target_user_id: nextId },
      })
      if (!response.ok) {
        setCurrent(previous)
      } else {
        onAfterChange()
      }
    } catch {
      setCurrent(previous)
    } finally {
      setPending(false)
    }
  }

  return (
    <Select
      value={current === null ? '' : String(current)}
      onValueChange={handleChange}
      disabled={pending}
    >
      <SelectTrigger className="h-7 w-auto min-w-[140px] gap-1.5 border-0 px-2 text-sm font-normal text-foreground hover:bg-accent">
        <SelectValue placeholder={currentLabel} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={String(opt.id)}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
